/**
 * ProjectDetailPage component
 * Shows project details with file upload and list
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import projectsService from '../../services/projects'
import filesService from '../../services/files'
import useFileUpload from '../../hooks/useFileUpload'
import toast from 'react-hot-toast'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import FileUploadZone from '../../components/projects/FileUploadZone'
import FileList from '../../components/projects/FileList'
import MemberManagement from '../../components/projects/MemberManagement'
import Button from '../../components/common/Button'
import CreateAnalysisModal from '../../features/analyses/components/CreateAnalysisModal'

const ProjectDetailPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [project, setProject] = useState(null)
  const [files, setFiles] = useState([])
  const [deletedFiles, setDeletedFiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [filesLoading, setFilesLoading] = useState(false)
  const [error, setError] = useState('')
  const [showMemberManagement, setShowMemberManagement] = useState(false)
  const [showCreateAnalysis, setShowCreateAnalysis] = useState(false)

  const { uploads, uploadFiles } = useFileUpload(id)

  useEffect(() => {
    loadProject()
    loadFiles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const loadProject = async () => {
    try {
      setLoading(true)
      setError('')
      const data = await projectsService.getProject(id)
      setProject(data)
    } catch (err) {
      console.error('Error loading project:', err)
      if (err.response?.status === 404) {
        setError('Project not found')
        toast.error('Project not found')
        setTimeout(() => navigate('/projects'), 2000)
      } else {
        setError(err.response?.data?.detail || 'Failed to load project')
        toast.error('Failed to load project')
      }
    } finally {
      setLoading(false)
    }
  }

  const loadFiles = async () => {
    try {
      setFilesLoading(true)

      // Load active files
      const activeData = await filesService.listFiles(id, false)
      setFiles(activeData.results || activeData)

      // Load deleted files
      const deletedData = await filesService.listFiles(id, true)
      const allFiles = deletedData.results || deletedData
      const onlyDeleted = allFiles.filter((f) => f.is_deleted)
      setDeletedFiles(onlyDeleted)
    } catch (err) {
      console.error('Error loading files:', err)
      toast.error('Failed to load files')
    } finally {
      setFilesLoading(false)
    }
  }

  const handleFilesSelected = async (selectedFiles) => {
    await uploadFiles(selectedFiles)
    // Reload files after a short delay to show new uploads
    setTimeout(loadFiles, 1000)
  }

  const handleFileDeleted = async () => {
    // Reload files from server to get the updated state
    await loadFiles()
  }

  const handleFileRestored = (restoredFile) => {
    // Move file from deleted list to active list
    setDeletedFiles((prevDeleted) => prevDeleted.filter((f) => f.id !== restoredFile.id))
    setFiles((prevFiles) => [...prevFiles, restoredFile])
  }

  const handleMemberCountChange = (newCount) => {
    // Update the project's member count
    setProject((prev) => ({
      ...prev,
      member_count: newCount,
    }))
  }

  // Combine uploaded files and files being uploaded
  // Filter out uploads that are already in the files list (to avoid duplicates)
  const fileIds = new Set(files.map((f) => f.id))
  const activeUploads = uploads.filter((upload) => !fileIds.has(upload.id))

  const allFiles = [
    ...activeUploads.map((upload) => ({
      id: upload.id,
      name: upload.name,
      size: upload.size,
      status: upload.status,
      error: upload.error,
      progress: upload.progress,
    })),
    ...files,
  ].filter((f) => !f.is_deleted) // Ensure deleted files don't show in main list

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <p className="text-red-600">{error || 'Project not found'}</p>
          <Link
            to="/projects"
            className="text-primary-600 hover:text-primary-700 mt-4 inline-block"
          >
            ← Back to Projects
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/projects"
          className="text-primary-600 hover:text-primary-700 text-sm font-medium mb-4 inline-block"
        >
          ← Back to Projects
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
            {project.description && <p className="mt-2 text-gray-600">{project.description}</p>}
            <div className="mt-3 flex items-center gap-4 text-sm text-gray-500">
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                {project.user_role}
              </span>
              <button
                onClick={() => setShowMemberManagement(true)}
                className="flex items-center gap-1 hover:text-gray-900 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                {project.member_count || 0} {project.member_count === 1 ? 'member' : 'members'}
              </button>
            </div>
          </div>
          <div className="flex gap-3">
            {(project.user_role === 'owner' || project.user_role === 'editor') && (
              <Button onClick={() => setShowCreateAnalysis(true)} size="sm" variant="primary">
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                Create Analysis
              </Button>
            )}
            {project.user_role === 'owner' && (
              <Button onClick={() => setShowMemberManagement(true)} size="sm">
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Manage Members
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Member Management Modal */}
      <MemberManagement
        isOpen={showMemberManagement}
        onClose={() => setShowMemberManagement(false)}
        projectId={id}
        userRole={project.user_role}
        currentUserId={user?.id}
        onMemberCountChange={handleMemberCountChange}
      />

      {/* Create Analysis Modal */}
      <CreateAnalysisModal
        isOpen={showCreateAnalysis}
        onClose={() => setShowCreateAnalysis(false)}
        projectId={id}
        projectName={project.name}
      />

      {/* File Upload Section */}
      {(project.user_role === 'owner' || project.user_role === 'editor') && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Upload Files</h2>
          <FileUploadZone onFilesSelected={handleFilesSelected} />
        </div>
      )}

      {/* Files Section */}
      <div>
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Files{' '}
            {allFiles.length > 0 && (
              <span className="text-gray-500 font-normal">({allFiles.length})</span>
            )}
          </h2>
        </div>

        {filesLoading && uploads.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : allFiles.length === 0 && deletedFiles.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No files</h3>
            <p className="mt-1 text-sm text-gray-500">
              {project.user_role === 'owner' || project.user_role === 'editor'
                ? 'Upload files to get started'
                : 'No files have been uploaded yet'}
            </p>
          </div>
        ) : (
          <FileList
            files={allFiles}
            deletedFiles={deletedFiles}
            userRole={project.user_role}
            currentUserId={user?.id}
            onFileDeleted={handleFileDeleted}
            onFileRestored={handleFileRestored}
          />
        )}
      </div>
    </div>
  )
}

export default ProjectDetailPage
